import { atom, type RecoilState } from 'recoil';
import { sessionStoragePersisterEffect } from 'utils/recoilEffects';
import type { DicomTransfer, DicomTransferJob } from './types';

/**
 * REVIEW.md keeps PHI out of browser storage, so the identity is dropped on the way out.
 *
 * `rejectionReason` goes too: it is the destination's own text about a study it refused, the same
 * kind of value `dropErrorDescriptions` removes from the jobs atom. `submitError` stays, since that
 * is our own copy rather than anything the backend said.
 */
const dropUnstorableText = (transfers: readonly DicomTransfer[]): readonly DicomTransfer[] =>
  transfers.map((transfer) => ({
    ...transfer,
    patientName: null,
    patientAge: null,
    accessionNumber: null,
    rejectionReason: null,
  }));

/** Nothing polled while the tab was away, so the stuck-timeout gets a fresh window. */
const restartStuckTimeout = (transfers: readonly DicomTransfer[]): readonly DicomTransfer[] => {
  const restoredAt = Date.now();
  return transfers.map((transfer) => ({ ...transfer, restoredAt }));
};

/** Survives a refresh minus the identity, which `useRestoreTransferIdentity` fetches back. */
export const dicomTransfersState: RecoilState<readonly DicomTransfer[]> = atom<
  readonly DicomTransfer[]
>({
  key: 'dicomSendTransfers',
  default: [],
  effects: [
    sessionStoragePersisterEffect({
      transformBeforeSave: dropUnstorableText,
      transformAfterLoad: restartStuckTimeout,
    }),
  ],
});

/** Backend error text is passed through verbatim and can name the instance, so it is not stored. */
const dropErrorDescriptions = (
  jobs: Readonly<Record<string, DicomTransferJob>>
): Readonly<Record<string, DicomTransferJob>> =>
  Object.fromEntries(
    Object.entries(jobs).map(([smid, job]) => [smid, { ...job, errorDescription: null }])
  );

export const dicomTransferJobsState: RecoilState<Readonly<Record<string, DicomTransferJob>>> = atom<
  Readonly<Record<string, DicomTransferJob>>
>({
  key: 'dicomSendTransferJobs',
  default: {},
  effects: [sessionStoragePersisterEffect({ transformBeforeSave: dropErrorDescriptions })],
});

/**
 * Hides the toolbar chip without discarding what it was tracking, so the history is still there on
 * the next send. Persisted, or a refresh would bring the chip straight back.
 */
export const isDicomTransfersDismissedState: RecoilState<boolean> = atom<boolean>({
  key: 'dicomSendTransfersDismissed',
  default: false,
  effects: [sessionStoragePersisterEffect()],
});

/**
 * Shared rather than local to a row's case menu: that menu unmounts on close and would take the
 * panel with it, leaving no way back to a running transfer. Not persisted, so a refresh does not
 * reopen the panel on its own.
 */
export const isDicomTransfersPanelOpenState: RecoilState<boolean> = atom<boolean>({
  key: 'dicomSendTransfersPanelOpen',
  default: false,
});
